import React, { useCallback, FC, ReactElement } from 'react';
import { Field, FieldProps } from 'formik';
import _findIndex from 'lodash/findIndex';
import _filter from 'lodash/filter';
import { setFieldValueAndTouched } from '@minecraft.utils';
import Checkbox from '@minecraft.checkbox';

type Props = {
  saveAsOption?: boolean;
  label: string | ReactElement;
  groupName: string;
  value: any;
  disabled?: boolean;
  children?: any;
  onChange?: (e?: any, value?: any[]) => void;
  className?: string;
  noMargin?: boolean;
};

const isChecked = (saveAsOption: boolean, fieldValue: any, actualValue: any): boolean => {
  if (fieldValue === undefined) return false;

  if (Array.isArray(actualValue)) {
    return actualValue.every(
      (v) => _findIndex(fieldValue, (item) => (saveAsOption ? (item as any)?.value === v.value : item === v)) !== -1
    );
  }

  return _findIndex(fieldValue, (item) => (item as any)?.value === actualValue || item === actualValue) !== -1;
};

/**
 * @deprecated This is an older component that does not use our standard form wrapper
 * to match the design system.
 *
 * If this component is used in application code, we want to replace it.
 *
 * If it's used for a boolean field in the formik form use ConnectedCheckboxInput
 * If it's used for a list of values in the formik form use CheckboxGroupInput
 *
 * import { ConnectedCheckboxInput, CheckboxGroupInput } from '@minecraft.molecules'
 *
 * // where fiieldName is a boolean value in the formik state
 * <ConnectedCheckboxInput name="fieldName" label="Field Label" />
 *
 * // where fiieldName is a list of values in the formik state
 * <CheckboxGroupInput name="fieldName" label="Field Label" options={[{ value: '1', label: 'option 1' }, { value: '2', label: 'option 2' }]} />
 * for any props that are not currently implemented we should add the functionality
 */
const FormikCheckbox: FC<Props & FieldProps> = ({
  saveAsOption = false,
  label,
  groupName,
  disabled,
  value: actualValue,
  field,
  form,
  onChange,
  ...restProps
}) => {
  const handleCheckboxChange = useCallback(
    (e) => {
      let newValue;

      if (isChecked(saveAsOption, field.value, actualValue)) {
        newValue = _filter(field.value, (valueItem) => {
          if (Array.isArray(actualValue)) {
            return (
              _findIndex(actualValue, (item) =>
                saveAsOption ? item.value === valueItem.value : item === valueItem
              ) === -1
            );
          }

          return saveAsOption ? valueItem.value !== actualValue : valueItem !== actualValue;
        });
      } else {
        newValue = field.value.concat(saveAsOption ? { label, value: actualValue } : actualValue);
      }

      setFieldValueAndTouched(groupName, newValue, form);

      // calling custom onChange for extending top functionality
      onChange && onChange(e, newValue);
    },
    [form, groupName, field, actualValue, onChange]
  );

  return (
    <Checkbox
      isChecked={isChecked(saveAsOption, field.value, actualValue)}
      label={label}
      disabled={disabled}
      onChange={handleCheckboxChange}
      name={groupName}
      value={actualValue}
      {...restProps}
    />
  );
};

export default function (props: Props): JSX.Element {
  return (
    <Field name={props.groupName} {...props}>
      {(fieldProps): JSX.Element => <FormikCheckbox {...props} {...fieldProps} />}
    </Field>
  );
}
